/**
 * Dynamic quad batching (W2-01a preview-gl; ARCHITECTURE.md §7.3 step 4).
 *
 * One dynamic vertex buffer (20 bytes per vertex: position f32 x2, UV f32 x2, colour u8 x4) with a static quad index
 * buffer; the buffer is orphaned on every flush (`bufferData` with exactly the used bytes) so the driver never waits
 * for the previous draw. Quads accumulate while program, texture, transform and blend mode stay the same; any change
 * flushes, so draw order is exactly push order. A note bucket is one draw call.
 *
 * Colours are bytes, explicitly CLAMPED before they are written (`colorByte`): a Uint8Array would wrap modulo 256.
 */

import type { Mat2D } from '../../core/util/types';
import { type BlendMode, type GlState, mat3Of, sameMat } from './glState';
import type { LinkedProgram } from './program';
import { ATTR } from './shaders';

export const VERTEX_BYTES = 20;
export const QUAD_BYTES = VERTEX_BYTES * 4;
const FLOATS_PER_VERTEX = VERTEX_BYTES / 4;
const INITIAL_QUADS = 1024;
/** Largest batch (indices are u32, so this is a memory bound, not an index bound). */
export const MAX_BATCH_QUADS = 65536;

/** A colour channel 0..1 as a byte: clamped first (NaN -> 0), then rounded. */
export function colorByte(v: number): number {
  if (!(v > 0)) return 0;
  if (v >= 1) return 255;
  return Math.round(v * 255);
}

const IDENTITY: Mat2D = [1, 0, 0, 1, 0, 0];

export class QuadBatch {
  private readonly gl: WebGL2RenderingContext;
  private readonly state: GlState;
  private readonly vao: WebGLVertexArrayObject | null;
  private readonly vbo: WebGLBuffer | null;
  private ibo: WebGLBuffer | null;
  private cap = 0;
  private iboCap = 0;
  private f32 = new Float32Array(0);
  private u8 = new Uint8Array(0);
  private n = 0;
  private prog: LinkedProgram | null = null;
  private tex: WebGLTexture | null = null;
  private xf: Mat2D = IDENTITY;
  private blend: BlendMode = 'alpha';
  private readonly xfData = new Float32Array(9);
  /** Transform last uploaded per program (uniforms are program state). */
  private readonly uploadedXf = new WeakMap<WebGLProgram, Mat2D>();
  /** Draw calls and quads since the last `resetStats`. */
  drawCalls = 0;
  quads = 0;

  constructor(gl: WebGL2RenderingContext, state: GlState) {
    this.gl = gl;
    this.state = state;
    this.vao = gl.createVertexArray();
    this.vbo = gl.createBuffer();
    this.ibo = gl.createBuffer();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enableVertexAttribArray(ATTR.pos);
    gl.vertexAttribPointer(ATTR.pos, 2, gl.FLOAT, false, VERTEX_BYTES, 0);
    gl.enableVertexAttribArray(ATTR.uv);
    gl.vertexAttribPointer(ATTR.uv, 2, gl.FLOAT, false, VERTEX_BYTES, 8);
    gl.enableVertexAttribArray(ATTR.color);
    gl.vertexAttribPointer(ATTR.color, 4, gl.UNSIGNED_BYTE, true, VERTEX_BYTES, 16);
    this.grow(INITIAL_QUADS);
    gl.bindVertexArray(null);
  }

  resetStats(): void {
    this.drawCalls = 0;
    this.quads = 0;
  }

  /** Quads waiting for the next flush. */
  get pending(): number {
    return this.n;
  }

  private grow(quads: number): void {
    const cap = Math.min(MAX_BATCH_QUADS, Math.max(quads, this.cap * 2, INITIAL_QUADS));
    if (cap <= this.cap) return;
    const data = new ArrayBuffer(cap * QUAD_BYTES);
    new Uint8Array(data).set(this.u8.subarray(0, this.n * QUAD_BYTES));
    this.f32 = new Float32Array(data);
    this.u8 = new Uint8Array(data);
    this.cap = cap;
    if (cap > this.iboCap) this.buildIndices(cap);
  }

  private buildIndices(cap: number): void {
    const gl = this.gl;
    const idx = new Uint32Array(cap * 6);
    for (let q = 0; q < cap; q++) {
      const v = q * 4;
      const i = q * 6;
      idx[i] = v;
      idx[i + 1] = v + 1;
      idx[i + 2] = v + 2;
      idx[i + 3] = v;
      idx[i + 4] = v + 2;
      idx[i + 5] = v + 3;
    }
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    this.iboCap = cap;
  }

  setProgram(p: LinkedProgram): void {
    if (this.prog === p) return;
    this.flush();
    this.prog = p;
  }

  setTexture(t: WebGLTexture | null): void {
    if (this.tex === t) return;
    this.flush();
    this.tex = t;
  }

  /** Space -> clip matrix of the following quads. */
  setTransform(m: Mat2D): void {
    if (sameMat(this.xf, m)) return;
    this.flush();
    this.xf = [m[0], m[1], m[2], m[3], m[4], m[5]];
  }

  setBlend(mode: BlendMode): void {
    if (this.blend === mode) return;
    this.flush();
    this.blend = mode;
  }

  /** Reserve room for one quad (grows, or flushes when the batch is at its maximum). Returns its index. */
  private reserve(): number {
    if (this.n >= this.cap) {
      if (this.cap < MAX_BATCH_QUADS) this.grow(this.n + 1);
      else this.flush();
    }
    const at = this.n;
    this.n = at + 1;
    return at;
  }

  private vertex(
    q: number,
    k: number,
    x: number,
    y: number,
    u: number,
    v: number,
    r: number,
    g: number,
    b: number,
    a: number,
  ): void {
    const vi = q * 4 + k;
    const f = vi * FLOATS_PER_VERTEX;
    this.f32[f] = x;
    this.f32[f + 1] = y;
    this.f32[f + 2] = u;
    this.f32[f + 3] = v;
    const c = vi * VERTEX_BYTES + 16;
    this.u8[c] = r;
    this.u8[c + 1] = g;
    this.u8[c + 2] = b;
    this.u8[c + 3] = a;
  }

  /**
   * One quad, corners in loop order (c0, c1, c2, c3), each with its UV; colour bytes (already clamped) for all four
   * corners.
   */
  quad(
    x0: number,
    y0: number,
    u0: number,
    v0: number,
    x1: number,
    y1: number,
    u1: number,
    v1: number,
    x2: number,
    y2: number,
    u2: number,
    v2: number,
    x3: number,
    y3: number,
    u3: number,
    v3: number,
    r: number,
    g: number,
    b: number,
    a: number,
  ): void {
    const q = this.reserve();
    this.vertex(q, 0, x0, y0, u0, v0, r, g, b, a);
    this.vertex(q, 1, x1, y1, u1, v1, r, g, b, a);
    this.vertex(q, 2, x2, y2, u2, v2, r, g, b, a);
    this.vertex(q, 3, x3, y3, u3, v3, r, g, b, a);
  }

  /**
   * Axis-aligned rectangle [x0, x1] x [y0, y1] in the current space under the affine `m` (corners
   * (x0,y0) (x1,y0) (x1,y1) (x0,y1)), UVs (ua, va) at (x0, y0) and (ub, vb) at (x1, y1).
   */
  rect(
    m: Mat2D,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    ua: number,
    va: number,
    ub: number,
    vb: number,
    r: number,
    g: number,
    b: number,
    a: number,
  ): void {
    const m0 = m[0];
    const m1 = m[1];
    const m2 = m[2];
    const m3 = m[3];
    const m4 = m[4];
    const m5 = m[5];
    this.quad(
      m0 * x0 + m2 * y0 + m4,
      m1 * x0 + m3 * y0 + m5,
      ua,
      va,
      m0 * x1 + m2 * y0 + m4,
      m1 * x1 + m3 * y0 + m5,
      ub,
      va,
      m0 * x1 + m2 * y1 + m4,
      m1 * x1 + m3 * y1 + m5,
      ub,
      vb,
      m0 * x0 + m2 * y1 + m4,
      m1 * x0 + m3 * y1 + m5,
      ua,
      vb,
      r,
      g,
      b,
      a,
    );
  }

  /** Draw everything pending. */
  flush(): void {
    const n = this.n;
    if (n === 0) return;
    this.n = 0;
    const prog = this.prog;
    if (prog === null) return;
    const gl = this.gl;
    const st = this.state;
    st.useProgram(prog.program);
    st.setBlend(this.blend);
    if (this.uploadedXf.get(prog.program) !== this.xf) {
      const loc = prog.uniform('u_xf');
      if (loc !== null) gl.uniformMatrix3fv(loc, false, mat3Of(this.xf, this.xfData));
      this.uploadedXf.set(prog.program, this.xf);
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    // A fresh store of exactly the used size (orphaning): the driver never waits for the previous draw, and a large
    // capacity left from a dense frame does not cost an allocation of that size on every small flush.
    gl.bufferData(gl.ARRAY_BUFFER, this.u8.subarray(0, n * QUAD_BYTES), gl.STREAM_DRAW);
    gl.drawElements(gl.TRIANGLES, n * 6, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
    this.drawCalls++;
    this.quads += n;
  }

  /** Drop pending quads without drawing (frame aborted). */
  discard(): void {
    this.n = 0;
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteVertexArray(this.vao);
    gl.deleteBuffer(this.vbo);
    gl.deleteBuffer(this.ibo);
    this.ibo = null;
    this.f32 = new Float32Array(0);
    this.u8 = new Uint8Array(0);
    this.cap = 0;
  }
}
